import { useState, useEffect } from 'react';
import { Award, Shield, Plus, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function QualificationsStep({ data, onChange }: Props) {
  const [newCert, setNewCert] = useState('');
  const [newLicense, setNewLicense] = useState({ name: '', number: '', expiryDate: '' });
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [licenseErrors, setLicenseErrors] = useState({ name: '', number: '', expiryDate: '' });

  const certifications = data.certifications || [];
  const licenses = data.licenses || [];
  const insurance = data.insurance || { hasInsurance: false, provider: '', coverage: '' };

  useEffect(() => {
    // Cleanup on unmount
    return () => {};
  }, []);

  const addCertification = () => {
    if (newCert.trim()) {
      onChange({ ...data, certifications: [...certifications, newCert.trim()] });
      setNewCert('');
    }
  };

  const removeCertification = (cert: string) => {
    onChange({ ...data, certifications: certifications.filter((c: string) => c !== cert) });
  };

  const addLicense = () => {
    // Reset errors
    const errors = { name: '', number: '', expiryDate: '' };
    let hasError = false;

    // Validate license name
    if (!newLicense.name.trim()) {
      errors.name = 'License name is required';
      hasError = true;
    }

    // Validate license number
    if (!newLicense.number.trim()) {
      errors.number = 'License number is required';
      hasError = true;
    }

    // Validate expiry date (cannot be in the past)
    if (newLicense.expiryDate) {
      const expiryDate = new Date(newLicense.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (expiryDate < today) {
        errors.expiryDate = 'Expiry date cannot be in the past';
        hasError = true;
      }
    }

    setLicenseErrors(errors);

    if (hasError) {
      return;
    }
    
    onChange({
      ...data,
      licenses: [...licenses, { ...newLicense, id: Date.now().toString() }]
    });
    setNewLicense({ name: '', number: '', expiryDate: '' });
    setLicenseErrors({ name: '', number: '', expiryDate: '' });
    setShowLicenseForm(false);
  };

  const removeLicense = (id: string) => {
    onChange({ ...data, licenses: licenses.filter((l: any) => l.id !== id) });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Qualifications</h2>
        <p className="text-gray-600">Add your certifications, licenses, and insurance</p>
      </div>

      {/* Certifications */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Award className="w-4 h-4 inline mr-2" />
          Certifications *
        </label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newCert}
            onChange={(e) => setNewCert(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
            placeholder="e.g., Gas Safe Registered"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <Button onClick={addCertification}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {certifications.map((cert: string, idx: number) => (
            <Badge key={idx} variant="success" className="flex items-center gap-2">
              {cert}
              <button onClick={() => removeCertification(cert)} className="hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      {/* Licenses */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Professional Licenses
        </label>
        <div className="space-y-3 mb-3">
          {licenses.map((license: any) => (
            <div key={license.id} className="border border-gray-200 rounded-lg p-3 flex justify-between items-start">
              <div>
                <div className="font-medium text-gray-900">{license.name}</div>
                <div className="text-sm text-gray-600">License #: {license.number}</div>
                {license.expiryDate && (
                  <div className="text-xs text-gray-500">Expires: {license.expiryDate}</div>
                )}
              </div>
              <button onClick={() => removeLicense(license.id)} className="text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {showLicenseForm ? (
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div>
              <input
                type="text"
                placeholder="License Name *"
                value={newLicense.name}
                onChange={(e) => {
                  setNewLicense({ ...newLicense, name: e.target.value });
                  if (licenseErrors.name) setLicenseErrors({ ...licenseErrors, name: '' });
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                  licenseErrors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {licenseErrors.name && (
                <p className="text-xs text-red-600 mt-1">{licenseErrors.name}</p>
              )}
            </div>
            
            <div>
              <input
                type="text"
                placeholder="License Number *"
                value={newLicense.number}
                onChange={(e) => {
                  setNewLicense({ ...newLicense, number: e.target.value });
                  if (licenseErrors.number) setLicenseErrors({ ...licenseErrors, number: '' });
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                  licenseErrors.number ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {licenseErrors.number && (
                <p className="text-xs text-red-600 mt-1">{licenseErrors.number}</p>
              )}
            </div>
            
            <div>
              <input
                type="date"
                placeholder="Expiry Date (Optional)"
                value={newLicense.expiryDate}
                onChange={(e) => {
                  setNewLicense({ ...newLicense, expiryDate: e.target.value });
                  if (licenseErrors.expiryDate) setLicenseErrors({ ...licenseErrors, expiryDate: '' });
                }}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                  licenseErrors.expiryDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {licenseErrors.expiryDate && (
                <p className="text-xs text-red-600 mt-1">{licenseErrors.expiryDate}</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button onClick={addLicense}>
                Add License
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowLicenseForm(false);
                  setNewLicense({ name: '', number: '', expiryDate: '' });
                  setLicenseErrors({ name: '', number: '', expiryDate: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowLicenseForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add License
          </Button>
        )}
      </div>

      {/* Insurance */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Shield className="w-4 h-4 inline mr-2" />
          Insurance Information *
        </label>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={insurance.hasInsurance}
              onChange={(e) => onChange({
                ...data,
                insurance: { ...insurance, hasInsurance: e.target.checked }
              })}
              className="rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm text-gray-700">I have professional insurance</span>
          </label>
          {insurance.hasInsurance && (
            <div className="grid md:grid-cols-2 gap-4 pl-6">
              <input
                type="text"
                placeholder="Insurance Provider"
                value={insurance.provider}
                onChange={(e) => onChange({
                  ...data,
                  insurance: { ...insurance, provider: e.target.value }
                })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <input
                type="text"
                placeholder="Coverage Amount (e.g., €1,000,000)"
                value={insurance.coverage}
                onChange={(e) => onChange({
                  ...data,
                  insurance: { ...insurance, coverage: e.target.value }
                })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
